import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { useStore } from '../../../../../../stores/root';

// assets
import VesselRequisitionItemStatus from '../../../../../../manifests/status/vesselRequisitionItem';

// services
import PermissionsService from '../../../../../../services/permissions';
import GeneralService from '../../../../../../services/general';
import VesselRequisitionItemService from '../../../../../../services/vessels/requisitions/item';

// props
import { IVesselRequisitionResourceShortProps } from '../../../../../../props/vessels/requisitions/requisition';
import { IVesselRequisitionItemResourceShortProps } from '../../../../../../props/vessels/requisitions/item';

// components
import { ActionButton, CommandBar, Dropdown, IColumn, ICommandBarItemProps, ISelection, Selection, SelectionMode, ShimmeredDetailsList, Stack } from '@fluentui/react';
import Text from '../../../../../typography/text';
import Label from '../../../../../typography/label';
import Tag from '../../../../../uiframeworks/tag';
import ApproveVesselRequisitionItem from '../form/approve';
import RejectVesselRequisitionItem from '../form/reject';
import UpdateVesselRequisitionItemPricing from '../form/pricings/update';
import VesselRequisitionItemApprovalStatus from '../../../../../../manifests/status/vesselRequisitionItemApproval';
import VesselRequisitionItemPricingStatus from '../../../../../../manifests/status/vesselRequisitionItemPricing';
import VesselRequisitionItemDeliveryStatus from '../../../../../../manifests/status/vesselRequisitionItemDelivery';

interface VesselRequisitionItemsListProps {
    requisitionId: string;
}

const VesselRequisitionItemsList: React.FC<VesselRequisitionItemsListProps> = observer((props: VesselRequisitionItemsListProps) => {
    const { user } = useStore();

    const [allItems, setAllItems] = React.useState<IVesselRequisitionItemResourceShortProps[]>([]);
    const [filteredItems, setFilteredItems] = React.useState<IVesselRequisitionItemResourceShortProps[]>([]);
    const [selected, setSelected] = React.useState<IVesselRequisitionItemResourceShortProps[]>([]);
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();
    const [selectedStatus, setSelectedStatus] = React.useState<string>("all");

    const selection: ISelection = new Selection({
        onSelectionChanged: () => {
            setSelected(selection.getSelection() as IVesselRequisitionItemResourceShortProps[])
        },
    });

    const columns: IColumn[] = [
        {
            key: "name",
            name: 'Name',
            minWidth: 100,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Label size={'xsmall'}>{item.goods.name}</Label>
                    {item.notes ? <Text size={'small'} style={{ fontStyle: 'italic' }}>Notes: {item.notes}</Text> : null}
                </Stack>
            }
        },
        {
            key: "purpose",
            name: 'Purpose',
            minWidth: 200,
            maxWidth: 200,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text size={'small'}>{item.purpose}</Text>
                </Stack>
            }
        },
        {
            key: "size",
            name: 'Size (length x width x height)',
            minWidth: 200,
            maxWidth: 200,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }} horizontal tokens={{ childrenGap: 5 }}>
                    <Text size={'small'}>{GeneralService.getNumberWithSeparator(Number(item.goods?.length))} cm</Text>
                    <Text size={'small'}>x</Text>
                    <Text size={'small'}>{GeneralService.getNumberWithSeparator(Number(item.goods?.width))} cm</Text>
                    <Text size={'small'}>x</Text>
                    <Text size={'small'}>{GeneralService.getNumberWithSeparator(Number(item.goods?.height))} cm</Text>
                </Stack>
            }
        },
        {
            key: "weight",
            name: 'Weight',
            minWidth: 125,
            maxWidth: 125,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text size={'small'}>{GeneralService.getNumberWithSeparator(Number(item.goods?.weightAmount))} {item.goods?.weightUnit}</Text>
                </Stack>
            }
        },
        {
            key: "quantity",
            name: 'Quantity',
            minWidth: 125,
            maxWidth: 125,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text size={'small'}>{GeneralService.getNumberWithSeparator(Number(item.quantityAmount))} {item.quantityUnit.name}</Text>
                </Stack>
            }
        },
        {
            key: "approvalStatus",
            name: "Approval",
            minWidth: 75,
            maxWidth: 75,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                const status = VesselRequisitionItemApprovalStatus.find((r) => r.key === item.approvalStatus);

                return <Stack horizontal styles={{ root: { padding: '0px' } }}>
                    <Tag size={'small'} text={status?.text} variant={status?.tagVariant} />
                </Stack>
            }
        },
        {
            key: "pricingStatus",
            name: "Pricing",
            minWidth: 75,
            maxWidth: 75,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                const status = VesselRequisitionItemPricingStatus.find((r) => r.key === item.pricingStatus);

                return <Stack horizontal styles={{ root: { padding: '0px' } }}>
                    {item.approvalStatus === 'approved' ? <Tag size={'small'} text={status?.text} variant={status?.tagVariant} /> : null}
                    {item.approvalStatus !== 'approved' ? <Text size={'small'}>-</Text> : null}
                </Stack>
            }
        },
        {
            key: "paymentStatus",
            name: "Payment",
            minWidth: 75,
            maxWidth: 75,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                const status = VesselRequisitionItemPricingStatus.find((r) => r.key === item.paymentStatus);

                return <Stack horizontal styles={{ root: { padding: '0px' } }}>
                    {item.pricingStatus === 'approved' ? <Tag size={'small'} text={status?.text} variant={status?.tagVariant} /> : null}
                    {item.pricingStatus !== 'approved' ? <Text size={'small'}>-</Text> : null}
                </Stack>
            }
        },
        {
            key: "deliveryStatus",
            name: "Delivery",
            minWidth: 75,
            maxWidth: 75,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                const status = VesselRequisitionItemDeliveryStatus.find((r) => r.key === item.deliveryStatus);

                return <Stack horizontal styles={{ root: { padding: '0px' } }}>
                    {item.pricingStatus === 'approved' ? <Tag size={'small'} text={status?.text} variant={status?.tagVariant} /> : null}
                    {item.pricingStatus !== 'approved' ? <Text size={'small'}>-</Text> : null}
                </Stack>
            }
        }
    ];

    useEffect(() => {
        _onRetrieveItems();
    }, []);

    const getCommandBarItems = () => {
        const items: ICommandBarItemProps[] = [];
        const farItems: ICommandBarItemProps[] = [];

        const canApprove = !(!loaded || selected.length < 1 || (selected.findIndex((s) => s.approvalStatus !== 'rejected' && s.approvalStatus !== 'pending') > -1));
        if (PermissionsService.hasPermission(['vessels.requisitions.approvals.approve_item'], user.permissions) && canApprove) {
            items.push({
                key: 'approve',
                text: 'Approve',
                onRender: () => {
                    return <ActionButton iconProps={{ iconName: "CheckMark" }}
                        text={'Approve items'}
                        styles={{ root: { marginRight: 10 } }}
                        onClick={() => setActiveSurface('items.approve')} />
                }
            });
        }

        const canReject = !(!loaded || selected.length < 1 || (selected.findIndex((s) => s.approvalStatus === 'rejected') > -1));
        if (PermissionsService.hasPermission(['vessels.requisitions.approvals.reject_item'], user.permissions) && canReject) {
            items.push({
                key: 'reject',
                text: 'Reject',
                onRender: () => {
                    return <ActionButton iconProps={{ iconName: "Cancel" }}
                        text={'Reject items'}
                        styles={{ root: { marginRight: 10 } }}
                        onClick={() => setActiveSurface('items.reject')} />
                }
            });
        }

        const canUpdatePrice = loaded && selected.length > 0 && (selected.findIndex((s) => s.approvalStatus !== 'approved') < 0);
        if (PermissionsService.hasPermission(['vessels.requisitions.pricings.update'], user.permissions) && canUpdatePrice) {
            items.push({
                key: 'updatePrice',
                text: 'Update price',
                onRender: () => {
                    return <ActionButton iconProps={{ iconName: "UpdatePrice" }}
                        text={'Update price'}
                        styles={{ root: { marginRight: 10 } }}
                        onClick={() => setActiveSurface('items.pricing.update')} />
                }
            });
        }

        farItems.push({
            key: 'filter',
            text: 'Filter',
            onRender: () => {
                return <Stack tokens={{ childrenGap: 5 }} horizontal verticalAlign={'center'}>
                    <Text size={'small'}>Select status: </Text>
                    <Dropdown options={[{ key: "all", text: "All status" }, ...VesselRequisitionItemStatus]}
                        styles={{ root: { width: 150 } }}
                        selectedKey={selectedStatus}
                        onChange={(evt, opt) => {
                            const status = opt?.key as string;
                            setSelectedStatus(status);
                            setSelected([]);
                            setFilteredItems(allItems.filter((i) => status === 'all' || status === i.approvalStatus));
                        }} />
                </Stack>
            }
        })

        return { items, farItems };
    }

    const _onRetrieveItems = async () => {
        setLoaded(false);

        const qs: string[] = ['top=all'];
        const _items = await VesselRequisitionItemService.retrieve(props.requisitionId, qs.join("&"));

        setAllItems(_items.data);
        setSelected([]);
        setFilteredItems(_items.data.filter((i) => selectedStatus === 'all' || selectedStatus === i.approvalStatus));
        setLoaded(true);
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        if (refresh) { _onRetrieveItems(); }
        setActiveSurface(undefined);
    }

    return <Stack>
        <CommandBar {...getCommandBarItems()} styles={{ root: { padding: 0 } }} />
        <ShimmeredDetailsList items={filteredItems}
            columns={columns}
            selection={selection}
            shimmerLines={5}
            enableShimmer={!loaded}
            selectionMode={SelectionMode.multiple} />
        {activeSurface === 'items.approve' ? <ApproveVesselRequisitionItem requisitionId={props.requisitionId} items={selected} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'items.reject' ? <RejectVesselRequisitionItem requisitionId={props.requisitionId} items={selected} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'items.pricing.update' ? <UpdateVesselRequisitionItemPricing items={selected} onDismissed={_onSurfaceDismissed} /> : null}
    </Stack>;
});

export default VesselRequisitionItemsList;
